import type { CheckoutTimingTracker } from "./analytics";
import type { BnplSubmitResult, SupersedeMode } from "./payment-methods/bnpl-lifecycle";
import type { MandateConsentAppearance } from "./payment-methods/next-action-handlers";
import type { RadarSessionMintHandle } from "./payment-methods/stripe-radar-session";
import type { CardErrorCode } from "./penpal/connect-card";
import type { TunnelXIFrameConnection } from "./penpal/connect-tunnel-x";
import type { SessionConfigTimeoutError } from "./session-config-fetch";

export type PaymentKitEnvironment = "local" | "tunnel" | "sandbox" | "staging" | "production";

type EnvironmentUrls = {
  baseUrl: string;
  apiBaseUrl: string;
};

// TODO: Cleanup this after launch to only use SDK instead of plain fetch.
const STATIC_ENVIRONMENT_URLS: Record<Exclude<PaymentKitEnvironment, "tunnel">, EnvironmentUrls> = {
  local: {
    baseUrl: "http://localhost:9101",
    apiBaseUrl: "http://localhost:9000",
  },
  sandbox: {
    baseUrl: "https://app.paymentkit.com/customer",
    apiBaseUrl: "https://app.paymentkit.com",
  },
  staging: {
    baseUrl: "https://staging.paymentkit.com/customer",
    apiBaseUrl: "https://staging.paymentkit.com",
  },
  production: {
    baseUrl: "https://app.paymentkit.com/customer",
    apiBaseUrl: "https://app.paymentkit.com",
  },
};

function getTunnelUrls(): EnvironmentUrls {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const apiBaseUrl = params.get("api_base_url");
    if (apiBaseUrl) {
      return {
        baseUrl: window.location.origin,
        apiBaseUrl,
      };
    }
    // For loclx-style tunnels where API hostname is derived from the current hostname
    const hostname = window.location.hostname;
    if (hostname.includes("loclx.io")) {
      const apiHostname = hostname.replace(/cportal|playground/, "api");
      return {
        baseUrl: `https://${hostname}`,
        apiBaseUrl: `https://${apiHostname}`,
      };
    }
  }
  // Fallback
  return STATIC_ENVIRONMENT_URLS.local;
}

// Normalize a merchant-supplied base URL to a bare https origin (scheme + host + port), dropping
// any path/query/hash. Returns undefined for anything that isn't a well-formed https origin, so the
// caller can fall back to the environment default rather than route traffic at a bad URL.
function normalizeHttpsOrigin(raw: string): string | undefined {
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return undefined;
    return url.origin;
  } catch {
    return undefined;
  }
}

/**
 * Resolve the URLs for a PaymentKit instance.
 *
 * `apiBaseUrlOverride` lets a merchant route all FIRST-PARTY SDK traffic at their own (unlisted) custom domain
 * to bypass a DNS/SURBL block of app.paymentkit.com (PAY custom-domain cure). It is the SINGLE
 * derivation point: both URLs are returned from here, threaded through `paymentKitStates`, so every
 * network host (config fetch, /ingest, the tunnel-x + card iframes, cardCheckout/CSI/wallet/BNPL/
 * SEPA/Link endpoints) honors it automatically. A single origin is enough — the custom domain
 * serves both the API and the embeds off one host, mirroring prod (apiBaseUrl = origin,
 * baseUrl = origin + "/customer").
 *
 * Additive and safe: when omitted → exactly today's environment-derived URLs (existing merchants
 * unaffected). When present but not a well-formed https origin → we warn and fall back to the
 * environment default rather than throw. It MUST be a constructor option, never read from the
 * session-config response — that fetch is itself blocked (chicken-and-egg).
 */
export function getUrlsForEnvironment(
  environment: PaymentKitEnvironment,
  apiBaseUrlOverride?: string,
): EnvironmentUrls {
  if (apiBaseUrlOverride !== undefined) {
    const origin = normalizeHttpsOrigin(apiBaseUrlOverride);
    if (origin) {
      return { apiBaseUrl: origin, baseUrl: `${origin}/customer` };
    }
    console.warn(
      `[PaymentKit] Ignoring invalid apiBaseUrl "${apiBaseUrlOverride}" (must be an https origin) — using the "${environment}" default`,
    );
  }
  if (environment === "tunnel") {
    return getTunnelUrls();
  }
  const urls = STATIC_ENVIRONMENT_URLS[environment];
  if (!urls) {
    console.warn(`[PaymentKit] Invalid environment "${environment}", falling back to local`);
    return STATIC_ENVIRONMENT_URLS.local;
  }
  return urls;
}

type FormFieldNames =
  | "customer_name"
  | "customer_email"
  | "customer_country"
  | "customer_zip_code"
  | "customer_business_name"
  | "customer_address_line1"
  | "customer_address_line2"
  | "customer_city"
  | "customer_state"
  | "customer_tax_ids"
  | "shipping_address_line1"
  | "shipping_address_line2"
  | "shipping_city"
  | "shipping_state"
  | "shipping_zip_code"
  | "shipping_country";

export type FormErrorCodes = "required" | "invalid";

export type CardSubmitOptions = {
  skipCustomerValidation?: boolean;
  /**
   * Extra params passed through to customer creation on the backend (PAY-1671 / #3785).
   * A free-form map; only whitelisted keys are honored server-side. Today the only honored
   * key is `processor_overrides` — the per-processor email override (Stripe only), e.g.
   * `{ processor_overrides: { proc_prod_xxx: { email: 'institution@university.edu' } } }`.
   * The customer's primary email is never mutated.
   */
  extraParamsForCustomerCreation?: Record<string, unknown>;
  /**
   * RBI e-mandate acceptance for an India-issued recurring checkout (PAY-1716). Set ONLY on
   * the RESUBMIT after the payer ticks the consent checkbox that the `stripe_mandate_consent`
   * next_action revealed. When present, the card submit forwards the consent contract
   * (`india_mandate_accepted` + `india_mandate_consent_version`) in the checkout request body
   * so the backend records the acceptance and proceeds into the single-step SetupIntent
   * mandate registration. Absent on the first submit and on every non-India checkout.
   */
  indiaMandateConsent?: {
    accepted: boolean;
    /** The version the next_action displayed (server `consentVersion`), stored with the evidence. */
    consentVersion: string;
  };
  /**
   * How PKJS presents the RBI e-mandate consent when the checkout pauses on the
   * `stripe_mandate_consent` next_action (PAY-1716). Only consulted on that pause.
   *   - `"modal"` (DEFAULT): PKJS auto-mounts a dialog with the terms + the verbatim
   *     consent text + an unchecked box, and on Accept resubmits the acceptance itself —
   *     zero integration, like 3DS.
   *   - `"inline"`: render the same content into `container` for a merchant that wants it
   *     inside its own page flow (falls back to the modal if `container` can't be resolved).
   *   - `"manual"`: return the action to the caller so the merchant renders its OWN
   *     unchecked-by-default gate and resubmits via
   *     `kit.submit({ options: { indiaMandateConsent: { accepted: true, consentVersion } } })`.
   * In every mode consent is affirmative: PKJS never auto-ticks and never auto-resubmits.
   */
  indiaMandateConsentUx?: {
    mode?: "modal" | "inline" | "manual";
    /** Required for `mode: "inline"` — the element (or CSS selector) to mount the consent into. */
    container?: HTMLElement | string;
    /**
     * Optional color overrides for the `modal`/`inline` consent surface (nice-to-have). Each key
     * sets one of the surface's theme CSS vars, reusing the hosted-checkout `--pk-brand` /
     * `--pk-brand-contrast` names — the SAME theming interface — so a plain PKJS embedder can
     * color-match the modal to its own page. Omitted → clean neutral defaults; ignored in
     * `manual` mode (the merchant renders its own surface). Keep `brandText` readable on `brand`.
     */
    appearance?: MandateConsentAppearance;
  };
};

/**
 * Merchant-pinned express-wallet processors, keyed by payment method (PAY-1701). Ids only — the
 * processor family (Stripe/Airwallex) is resolved from the account.
 */
export type WalletProcessorIds = { applePay?: string; googlePay?: string };

export type TInternalFuncs = {
  submitPayment: (fields: Partial<PaymentKitFields>, options?: unknown) => Promise<BnplSubmitResult>;
  cleanup?: () => void;
  /** BNPL only: ends a pending submit because the shopper started another payment method. */
  supersede?: (mode: SupersedeMode) => void;
};

export type PaymentKit = <T extends readonly PaymentMethod<unknown>[]>(options: {
  environment?: PaymentKitEnvironment;
  /**
   * Route all FIRST-PARTY SDK traffic at a single custom https origin (e.g. `https://pay.your-domain.com`)
   * instead of the `environment`-derived `app.paymentkit.com`. Use this to bypass a DNS/SURBL block
   * of app.paymentkit.com: point it at a custom domain you've provisioned (CNAME to
   * `cde.paymentkit.com`, status ACTIVE). Overrides both the API host and the embed/iframe host; the
   * custom domain serves both. Third-party hosts (Stripe/Adyen/VGS/Apple/Google CDNs, PostHog's UI
   * host) are not rewritten. Omit it for the default behavior. An invalid value (not an https origin)
   * is ignored (falls back to the environment default), never throws.
   *
   * Forward/backwards-compatible: OLDER PKJS versions destructure only the keys they know and
   * silently ignore this one, so the SAME init object works for an old↔new A/B — old versions keep
   * using app.paymentkit.com, new versions honor this override.
   */
  apiBaseUrl?: string;
  secureToken: string;
  paymentMethods: T;
  /** Card tokenization mode from checkout session */
  cardTokenizationMode?: "direct" | "vgs";
  /** VGS vault ID from checkout session */
  vgsVaultId?: string;
  /** VGS environment from checkout session */
  vgsEnvironment?: string;
  /**
   * Stripe publishable key used only to mint a Radar session (client IP/device) for
   * Visa CE 3.0 / Mastercard FPT dispute deflection. Auto-detected from the session
   * API when omitted. Cards are never sent to Stripe.js — collection stays on VGS.
   */
  stripeRadarPublishableKey?: string;
  /**
   * Pin which processor handles each express wallet, keyed by payment method. When an entry is
   * set, it OVERRIDES the backend's wallet processor selection, so that wallet's PaymentMethod
   * is minted AND charged on the given account (PAY-1701). Pass ids only — the processor family
   * (Stripe/Airwallex) is resolved from the account. Leave a wallet unset to auto-select its
   * express-checkout processor. Pass ids per checkout session to steer wallets per session
   * (e.g. EU vs US), and independently per wallet (Apple Pay and Google Pay resolve separately).
   *
   * @example
   * processorIds: { applePay: 'proc_prod_...eu', googlePay: 'proc_prod_...us' }
   */
  processorIds?: WalletProcessorIds;
  /**
   * Ask the Apple Pay and Google Pay sheets for the customer's shipping address. The address
   * is saved on the customer as their shipping address. Defaults to `false`.
   *
   * If your own form already has the shipping address, pass it in `fields`
   * (`shipping_address_line1`, `shipping_city`, ...) on `submit`; it wins over the sheet's.
   * To stop asking the sheet once your form has it, call
   * `paymentKit.apple_pay.setRequestShippingAddress(false)` (and the same on `google_pay`).
   */
  requestShippingAddress?: boolean;
  /** @internal Enable analytics test mode for e2e testing */
  __enableAnalyticsTestMode?: boolean;
}) => ExternalFuncsMapByPm<T> & {
  submit: PaymentKitSubmitHandler<T>;
  cleanup: () => void;
};

/**
 * Starts a payment. For BNPL methods, a second `submit()` while one is in flight with the same
 * options joins it, and only the first caller's callbacks fire. Different options return an error.
 */
type PaymentKitSubmitHandler<T extends readonly PaymentMethod<unknown>[]> = <
  N extends keyof ExternalFuncsMapByPm<T>,
>(options: {
  fields: Partial<PaymentKitFields>;
  paymentMethod: N;
  options?: unknown;
  onError: (error: PaymentKitErrors) => void;
  onSuccess: (data: { [key: string]: unknown }) => void;
}) => void;

export type PaymentKitStates = {
  baseUrl: string;
  apiBaseUrl: string;
  secureToken: string;
  environment: PaymentKitEnvironment;
  tunnelXConnection: TunnelXIFrameConnection;
  timingTracker: CheckoutTimingTracker;
  /** Checkout request ID for correlating all API calls and analytics events */
  checkoutRequestId: string;
  /** Card tokenization mode: 'direct' (KMS) or 'vgs' (VGS Collect + proxy) */
  cardTokenizationMode?: "direct" | "vgs";
  /** VGS vault ID (only when cardTokenizationMode is 'vgs') */
  vgsVaultId?: string;
  /** VGS environment: 'sandbox' or 'live' (only when cardTokenizationMode is 'vgs') */
  vgsEnvironment?: string;
  /**
   * Stripe publishable key for minting a Radar session at submit time (PAY-1726).
   * Undefined when no Stripe processor serves cards, in which case no Radar session
   * is minted and the charge proceeds unchanged.
   */
  stripeRadarPublishableKey?: string;
  /**
   * Merchant-pinned express-wallet processors by payment method (PAY-1701). When an entry is
   * set, wallet auto-prepare uses that id instead of the backend scan, so the wallet mints and
   * charges on the same account. The processor family is resolved from the account.
   */
  processorIds?: WalletProcessorIds;
  /** Whether wallet sheets start out asking for a shipping address (init option). */
  requestShippingAddress?: boolean;
  /** @internal Promise that resolves when session config (incl. VGS mode) is auto-detected.
   * ALWAYS resolves — a terminal failure is carried on `sessionConfigError`, never a rejection. */
  _sessionConfigReady?: Promise<void>;
  /** @internal Set when the session-config fetch fails terminally (no direct-mode fallback). Consumers
   * read this after awaiting `_sessionConfigReady` instead of catching a rejection. */
  sessionConfigError?: SessionConfigTimeoutError;
  /** @internal The session-config body from the init's one shared fetch, or `null` when that fetch
   * failed. ALWAYS resolves. Wallet auto-prepare reads it at mount instead of fetching the same
   * endpoint again (PAY-2447). */
  _initialSessionConfig?: Promise<unknown>;
  /** PaymentKit.js version, stamped on every card checkout's fraud metadata (PAY-2001). */
  clientVersion: string;
  /**
   * @internal The init's one Radar session mint (PAY-2001). Card submit waits on it,
   * bounded. Set synchronously during PaymentKit() init.
   */
  _radarSessionMint?: RadarSessionMintHandle;
  /**
   * @internal Whether the session config allows adding Stripe.js to the page. False
   * until the session config says otherwise; a failed fetch keeps it false.
   */
  /**
   * @internal Settles once the fraud fingerprint scripts have loaded or failed, or
   * once it is known there are none. Never rejects. Only card submit waits on it
   * (bounded) before building the body that carries the Airwallex device id;
   * wallets read the script state synchronously and never wait (PAY-1538).
   */
  _fraudScriptsReady?: Promise<void>;
  /**
   * Set by PaymentKit: ends any pending BNPL submit when another payment starts. submit() calls
   * it, and so does a method that charges outside submit() (Stripe Link confirms from its own button).
   */
  _onPaymentStarting?: (paymentMethodName: string) => void;
};

export type PaymentKitErrors = {
  root?: string;
  // The full checkout response carried alongside a terminal failure (error_code,
  // error_message_for_customer, …) so the merchant can render richer error detail.
  // Set by the card checkout loop on a concluded failure state.
  checkout_response?: unknown;
  card_pan?: CardErrorCode;
  card_exp?: CardErrorCode;
  card_cvc?: CardErrorCode;
  paypal?: string;
  google_pay?: string;
  apple_pay?: string;
  stripe_link?: string;
  processor_id?: string;
  amount?: string;
  currency?: string;
  country?: string;
} & { [key in FormFieldNames]?: FormErrorCodes | string };

export type PaymentKitFields = { [key in FormFieldNames]: string };

export type PaymentMethod<TExternalFuncs = unknown, TName = string> = (paymentKitStates: PaymentKitStates) => {
  name: TName;
  externalFuncs: TExternalFuncs;
  internalFuncs: TInternalFuncs;
};

export type ExternalFuncsMapByPm<T extends readonly PaymentMethod<unknown>[]> = {
  [K in T[number] as ReturnType<K>["name"]]: ReturnType<K>["externalFuncs"];
};
